import React, {
  useState,
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
  ForwardRefExoticComponent,
} from "react";
import ReactHtmlParser from "react-html-parser";
import "./components/index.scss";
import { postRequest } from "../../../../utils/httpRequest";

type Message = {
  role: string;
  // Markdown, as Deep Thought wrote it. This is what goes back to the server
  // as the conversation so far.
  content: string;
  // Deep Thought's replies, rendered and cleaned on the server. The learner's
  // own messages never have it and are shown as plain text.
  html?: string;
  id?: string;
};

type Props = {
  showAiChat: boolean;
  handleShowAiChat: (e, val) => void;
  // Sent with every message so Deep Thought knows the course, the lesson
  // open now, and the learner's answers so far.
  enrolmentId?: string;
  lessonId?: string;
};

const suggestedPrompts = [
  "Explain this lesson in plain English",
  "Give me a real-world example to make it stick",
  "How could I use this in my work?",
];

const AiChat: ForwardRefExoticComponent<Props> = forwardRef((props, ref) => {
  const { showAiChat, handleShowAiChat, enrolmentId, lessonId } = props;
  const startingMessage: Message = {
    role: "assistant",
    content:
      "Why hello there. I have read every course on 42courses, and I can see where you are in this one. Ask me anything.",
    id: undefined,
  };
  const [messages, setMessages] = useState<Message[]>([startingMessage]);
  const [userInput, setUserInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [maxMessagesReached, setMaxMessagesReached] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);

  useEffect(() => {
    setMaxMessagesReached(messages.length >= 15);
  }, [messages]);

  const messagesEndRef = useRef(null);
  const drawerRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading, showAiChat]);

  useEffect(() => {
    const handleDocumentClick = (e) => {
      if (
        showAiChat &&
        drawerRef.current &&
        !drawerRef.current.contains(e.target)
      ) {
        handleShowAiChat(e, false);
      }
    };

    document.addEventListener("mousedown", handleDocumentClick);

    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
    };
  }, [showAiChat, handleShowAiChat]);

  const sendMessage = (content: string) => {
    const trimmedContent = content.trim();

    if (!trimmedContent || loading || maxMessagesReached) {
      return;
    }

    const nextMessage = { role: "user", content: trimmedContent };
    const nextMessages = [...messages, nextMessage];
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    setMessages(nextMessages);
    setShowSuggestions(false);
    setLoading(true);
    setError(null);
    setUserInput("");

    postRequest("/api/ai/courses/deep_thought.json", {
      messages: nextMessages.map(({ role, content }) => ({ role, content })),
      enrolment_id: enrolmentId,
      lesson_id: lessonId,
    })
      .then((response) => {
        if (requestId !== requestIdRef.current) {
          return;
        }

        setLoading(false);

        // @ts-ignore
        const { data } = response;
        setMessages((prevMessages) => [...prevMessages, data]);
      })
      .catch((error) => {
        if (requestId !== requestIdRef.current) {
          return;
        }

        console.error(error);
        setError("Deep Thought had a wobble. Please try again.");
        setLoading(false);
      });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    sendMessage(userInput);
  };

  const resetConversation = (e?) => {
    e?.preventDefault();
    requestIdRef.current += 1;
    setMessages([
      {
        role: "assistant",
        content: "Fresh start. What would you like to know?",
      },
    ]);
    setUserInput("");
    setError(null);
    setLoading(false);
    setMaxMessagesReached(false);
    setShowSuggestions(false);
  };

  const toggleOpen = (e) => {
    handleShowAiChat(e, !showAiChat);
  };

  const closeChat = (e) => {
    handleShowAiChat(e, false);
  };

  const handleSuggestion = (prompt: string) => {
    sendMessage(prompt);
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      handleSubmit(e);
    }
  };

  useImperativeHandle(ref, () => ({
    toggleOpen,
  }));

  return (
    <div className="ai-lesson-chat">
      <aside
        className={`drawer-r ${showAiChat ? "is-open" : ""}`}
        id="aiDrawer"
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="aiTitle"
        aria-hidden={!showAiChat}
      >
        <div className="dt__head">
          <span className="dt__avatar">
            <svg aria-hidden="true">
              <use href="#i-spark" />
            </svg>
          </span>
          <div className="dt__id">
            <div className="dt__name" id="aiTitle">
              Deep Thought AI
            </div>
            <div className="dt__status">
              <span className="live" />
              Online
            </div>
          </div>
          <button
            className="drawer-close"
            type="button"
            aria-label="Close Deep Thought"
            onClick={closeChat}
          >
            <svg aria-hidden="true">
              <use href="#i-x" />
            </svg>
          </button>
        </div>

        <div className="dt__beta">
          <b>This is a beta AI system.</b> Please take the results with a pinch
          of salt.
        </div>

        <div className="dt__log" id="aiLog" aria-live="polite">
          {error && <div className="dt-msg dt-msg--ai">{error}</div>}
          {messages.map((message, index) => (
            <div
              className={`dt-msg ${
                message.role === "user" ? "dt-msg--me" : "dt-msg--ai"
              }`}
              key={message.id || index}
            >
              {message.html
                ? // @ts-ignore
                  ReactHtmlParser(message.html)
                : message.content}
            </div>
          ))}
          {showSuggestions && messages.length === 1 && (
            <div className="dt-suggest" aria-label="Suggested questions">
              {suggestedPrompts.map((prompt) => (
                <button
                  type="button"
                  key={prompt}
                  onClick={() => handleSuggestion(prompt)}
                >
                  {prompt}
                </button>
              ))}
            </div>
          )}
          {loading && <div className="dt-msg dt-msg--ai">Thinking...</div>}
          {maxMessagesReached && (
            <div className="dt-msg dt-msg--ai">
              Conversation limit reached. Start a new one to keep going.
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <form className="dt__input" onSubmit={handleSubmit}>
          <textarea
            rows={1}
            value={userInput}
            placeholder={
              maxMessagesReached
                ? "Conversation limit reached"
                : "Ask Deep Thought anything..."
            }
            aria-label="Message Deep Thought"
            onChange={(e) => setUserInput(e.target.value)}
            onKeyDown={handleInputKeyDown}
            disabled={loading || maxMessagesReached}
          />
          <button
            className="dt-reset"
            type="button"
            aria-label="Start a new conversation"
            onClick={resetConversation}
          >
            <svg aria-hidden="true">
              <use href="#i-refresh" />
            </svg>
          </button>
          <button
            className="dt-send"
            type="submit"
            aria-label="Send message"
            disabled={loading || maxMessagesReached || !userInput.trim()}
          >
            <svg aria-hidden="true">
              <use href="#i-send" />
            </svg>
          </button>
        </form>
      </aside>
    </div>
  );
});

export default AiChat;
